import React, { useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export interface XlsxExportData {
  [key: string]: any;
}

export interface XlsxExportColumn {
  key: string;
  header: string;
  width?: number;
  format?: (value: any) => string;
}

export interface XlsxExportProps {
  data: XlsxExportData[];
  columns: XlsxExportColumn[];
  filename?: string;
  sheetName?: string;
  buttonText?: string;
  buttonVariant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "destructive"
    | "link";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  disabled?: boolean;
  className?: string;
  loadingMessage?: string;
  successMessage?: (filename: string) => string;
  errorMessage?: (error: Error) => string;
}

export function XlsxExport({
  data,
  columns,
  filename = "export",
  sheetName = "Sheet1",
  buttonText = "Export to Excel",
  buttonVariant = "outline",
  buttonSize = "default",
  disabled = false,
  className,
  loadingMessage = "Starting export...",
  successMessage = (filename) => `Export completed: ${filename}`,
  errorMessage = (error) => `Failed to export: ${error.message}`,
}: XlsxExportProps) {
  const processedData = useMemo(() => {
    return data.map((row) => {
      const processedRow: Record<string, any> = {};
      columns.forEach((column) => {
        const value = row[column.key];
        processedRow[column.header] = column.format
          ? column.format(value)
          : value;
      });
      return processedRow;
    });
  }, [data, columns]);

  const exportFile = useCallback(async () => {
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(processedData);

    // Set column widths if specified
    const columnWidths = columns
      .map((col, index) => ({
        wch:
          col.width ||
          Math.max(
            col.header.length,
            ...processedData.map((row) => String(row[col.header] || "").length)
          ),
      }))
      .filter((width) => width.wch > 0);

    if (columnWidths.length > 0) {
      worksheet["!cols"] = columnWidths;
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    const finalFilename = `${filename}_${timestamp}.xlsx`;

    // Download file
    saveAs(blob, finalFilename);

    return Promise.resolve(finalFilename);
  }, [processedData, columns, filename, sheetName]);

  const handleExport = useCallback(() => {
    toast.promise(exportFile(), {
      loading: loadingMessage,
      success: (filename) => successMessage(filename),
      error: (error) => errorMessage(error),
    });
  }, [exportFile]);

  return (
    <Button
      variant={buttonVariant}
      size={buttonSize}
      onClick={handleExport}
      disabled={disabled || data.length === 0}
      className={className}
    >
      <Download className="mr-2 h-4 w-4" />
      {buttonText}
    </Button>
  );
}

// Hook for more complex export scenarios
export function useXlsxExport() {
  const exportToXlsx = useCallback(
    (
      data: XlsxExportData[],
      columns: XlsxExportColumn[],
      options: {
        filename?: string;
        sheetName?: string;
        onExportStart?: () => void;
        onExportComplete?: (filename: string) => void;
        onExportError?: (error: Error) => void;
      } = {}
    ) => {
      const {
        filename = "export",
        sheetName = "Sheet1",
        onExportStart,
        onExportComplete,
        onExportError,
      } = options;

      try {
        onExportStart?.();

        // Process data
        const processedData = data.map((row) => {
          const processedRow: Record<string, any> = {};
          columns.forEach((column) => {
            const value = row[column.key];
            processedRow[column.header] = column.format
              ? column.format(value)
              : value;
          });
          return processedRow;
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(processedData);

        // Set column widths
        const columnWidths = columns
          .map((col, index) => ({
            wch:
              col.width ||
              Math.max(
                col.header.length,
                ...processedData.map(
                  (row) => String(row[col.header] || "").length
                )
              ),
          }))
          .filter((width) => width.wch > 0);

        if (columnWidths.length > 0) {
          worksheet["!cols"] = columnWidths;
        }

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Generate file
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 10);
        const finalFilename = `${filename}_${timestamp}.xlsx`;

        // Download file
        saveAs(blob, finalFilename);

        onExportComplete?.(finalFilename);
      } catch (error) {
        console.error("xlsx export error:", error);
        onExportError?.(error as Error);
      }
    },
    []
  );

  return { exportToXlsx };
}
